import React, { useEffect, useState, useRef } from 'react';
import { X, CheckCircle2, MapPin } from 'lucide-react';
import './SignupPopup.css';

/**
 * Rotating "someone just signed up" social-proof pop-up.
 * - Mounts globally in App.js
 * - Starts after a short delay, then shows a random name/city/service combo
 * - Fades in bottom-left, auto-dismisses, waits, then shows the next
 * - Dismissible via the X button (pauses further pops for this session)
 */

const FIRST_NAMES = [
  'Jasmine', 'Marcus', 'Keisha', 'Terrance', 'Ashley', 'Darnell', 'Monica',
  'Rashad', 'Latoya', 'Jerome', 'Tiffany', 'Dominique', 'Brianna', 'Kevin',
  'Maya', 'Devin', 'Candace', 'Elijah', 'Nia', 'Trevor', 'Shanice', 'Isaiah',
  'Imani', 'Malik', 'Danielle', 'Jalen', 'Simone', 'Andre', 'Brittany',
  'Kendrick', 'Jada', 'Omar', 'Alicia', 'Darius', 'Zuri', 'Marquis',
  'Jordan', 'Alexis', 'Xavier', 'Taylor',
];

const LAST_INITIALS = ['B.', 'C.', 'D.', 'G.', 'H.', 'J.', 'K.', 'L.', 'M.', 'R.', 'S.', 'T.', 'W.'];

const CITIES = [
  'Atlanta, GA', 'Houston, TX', 'Dallas, TX', 'Charlotte, NC', 'Chicago, IL',
  'Detroit, MI', 'Memphis, TN', 'New Orleans, LA', 'Philadelphia, PA', 'Baltimore, MD',
  'Birmingham, AL', 'Jackson, MS', 'Richmond, VA', 'Columbia, SC', 'Nashville, TN',
  'Oakland, CA', 'St. Louis, MO', 'Cleveland, OH', 'Miami, FL', 'Washington, DC',
  'Phoenix, AZ', 'Las Vegas, NV', 'Newark, NJ', 'Brooklyn, NY', 'Indianapolis, IN',
];

const SERVICES = [
  'just joined the 75-Day Transformation',
  'just claimed the $1 Credit Monitoring trial',
  'just booked a VIP Day with the CEO',
  'just enrolled in Perfect Credit Monitoring',
  'just paid in full for the Elite $250K package',
  'just started AI Business Autopilot',
  'just started Student Loan Removal',
  'just locked in the Complete Stack',
  'just signed up for Done-For-You Credit Repair',
];

const TIME_PHRASES = [
  'just now',
  '2 min ago',
  '5 min ago',
  '7 min ago',
  '12 min ago',
  '18 min ago',
];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const buildNotification = () => ({
  id: Date.now() + Math.random(),
  name: `${pick(FIRST_NAMES)} ${pick(LAST_INITIALS)}`,
  city: pick(CITIES),
  service: pick(SERVICES),
  time: pick(TIME_PHRASES),
});

export const SignupPopup = ({
  startDelay = 8000,       // first popup after 8s
  showDuration = 6500,     // each popup visible for 6.5s
  gapRange = [14000, 26000], // gap between pops: 14-26s
}) => {
  const [current, setCurrent] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const timeoutsRef = useRef([]);

  const clearAll = () => {
    timeoutsRef.current.forEach((t) => clearTimeout(t));
    timeoutsRef.current = [];
  };

  useEffect(() => {
    if (dismissed) {
      clearAll();
      setCurrent(null);
      return;
    }

    const cycle = () => {
      setCurrent(buildNotification());
      // schedule hide
      const hideT = setTimeout(() => {
        setCurrent(null);
        // schedule next
        const [gMin, gMax] = gapRange;
        const nextGap = Math.random() * (gMax - gMin) + gMin;
        const nextT = setTimeout(cycle, nextGap);
        timeoutsRef.current.push(nextT);
      }, showDuration);
      timeoutsRef.current.push(hideT);
    };

    const firstT = setTimeout(cycle, startDelay);
    timeoutsRef.current.push(firstT);

    return clearAll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed]);

  if (!current) return null;

  return (
    <div className="tcf-signup-pop" data-testid="signup-popup" role="status" aria-live="polite">
      <span className="tcf-signup-pop-avatar">
        <CheckCircle2 size={18} />
      </span>
      <div className="tcf-signup-pop-body">
        <p className="tcf-signup-pop-headline">
          <strong>{current.name}</strong> {current.service}
        </p>
        <p className="tcf-signup-pop-meta">
          <MapPin size={11} /> {current.city} · <span className="tcf-signup-pop-time">{current.time}</span>
        </p>
      </div>
      <button
        type="button"
        className="tcf-signup-pop-close"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        data-testid="signup-popup-close"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default SignupPopup;
